import { useMemo } from 'react';
import { Link, useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Plus,
  Button,
  getVizIcon,
  formatRelativeTime,
  MoreVertical,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@deckspace/ui';
import { useAuth } from '@deckspace/client';
import { useApi } from '../lib/api-context';
import { fetchWorkspaces, type WorkspaceListItem } from '../lib/queries';
import { queryKeys } from '../lib/queryKeys';
import type { AppLayoutOutletContext } from '../layouts/AppLayout';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFirstName(user: { id: string; email?: string } | null): string | null {
  if (!user?.email) return null;
  return user.email.split('@')[0]!;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SKELETON_COUNT = 3;

function SkeletonGrid() {
  const placeholders = useMemo(() => Array.from({ length: SKELETON_COUNT }, (_, i) => i), []);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {placeholders.map((i) => (
        <div key={i} className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex gap-1.5">
            <div className="h-5 w-5 animate-pulse rounded bg-muted" />
            <div className="h-5 w-5 animate-pulse rounded bg-muted" />
            <div className="h-5 w-5 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

const MAX_MINIMAP_ICONS = 6;

interface WorkspaceCardProps {
  workspace: WorkspaceListItem;
  onDelete: (id: string) => void;
  onRename: (id: string) => void;
}

function WorkspaceCard({ workspace: ws, onDelete, onRename }: WorkspaceCardProps) {
  const vizTypeIcons = useMemo(
    () =>
      ws.vizTypes.slice(0, MAX_MINIMAP_ICONS).map((type) => ({
        type,
        Icon: getVizIcon(type),
      })),
    [ws.vizTypes]
  );

  return (
    <Link
      to={`/w/${ws.id}`}
      className="group relative rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40"
    >
      {/* Three-dot menu — visible on hover */}
      <div className="absolute top-2 right-2" onClick={(e) => e.preventDefault()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-muted rounded-md transition-opacity text-muted-foreground hover:text-foreground"
              title="Workspace options"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onRename(ws.id)}>Rename</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => onDelete(ws.id)}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {ws.vizTypes.length > 0 && (
        <div className="mb-3 flex items-center gap-1.5">
          {vizTypeIcons.map(({ type, Icon }) => (
            <div
              key={type}
              className="flex h-5 w-5 items-center justify-center rounded bg-muted"
              title={type}
            >
              <Icon className="h-3 w-3 text-muted-foreground" />
            </div>
          ))}
          {ws.vizTypes.length > MAX_MINIMAP_ICONS && (
            <span className="text-xs text-muted-foreground">
              +{ws.vizTypes.length - MAX_MINIMAP_ICONS}
            </span>
          )}
        </div>
      )}

      <h3 className="font-medium text-foreground group-hover:text-primary">
        {ws.name || 'Untitled workspace'}
      </h3>

      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          {ws.vizCount} {ws.vizCount === 1 ? 'viz' : 'vizs'}
        </span>
        <span>
          {ws.datasetCount} {ws.datasetCount === 1 ? 'dataset' : 'datasets'}
        </span>
        <span>
          {ws.pageCount} {ws.pageCount === 1 ? 'page' : 'pages'}
        </span>
      </div>

      <p className="mt-1 text-xs text-muted-foreground">
        Updated {formatRelativeTime(ws.updatedAt)}
      </p>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Home() {
  const { user } = useAuth();
  const api = useApi();
  const { onOpenNewWorkspace, onDeleteWorkspace, onRenameWorkspace } =
    useOutletContext<AppLayoutOutletContext>();
  const {
    data: workspaces = [],
    isPending,
    error,
    refetch,
  } = useQuery<WorkspaceListItem[]>({
    queryKey: queryKeys.workspaces.home(),
    queryFn: () => fetchWorkspaces(api),
    refetchInterval: 10_000,
  });

  const firstName = useMemo(() => getFirstName(user), [user]);
  const hasWorkspaces = workspaces.length > 0;

  const lastActive = useMemo(() => {
    if (!hasWorkspaces) return null;
    return formatRelativeTime(workspaces[0]!.updatedAt);
  }, [workspaces, hasWorkspaces]);

  return (
    <div className="h-full overflow-auto bg-background">
      <main className="mx-auto max-w-5xl px-4 py-8">
        {/* Welcome header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {firstName ? `Welcome back, ${firstName}!` : 'Welcome to Deckspace!'}
            </h1>
            {!isPending && hasWorkspaces && (
              <p className="mt-1 text-sm text-muted-foreground">
                {workspaces.length} {workspaces.length === 1 ? 'workspace' : 'workspaces'}
                {lastActive ? ` · Last active ${lastActive}` : ''}
              </p>
            )}
          </div>
          <Button onClick={onOpenNewWorkspace} className="shrink-0">
            <Plus className="mr-1.5 h-4 w-4" />
            New workspace
          </Button>
        </div>

        {/* Loading */}
        {isPending && <SkeletonGrid />}

        {/* Error */}
        {error && !isPending && (
          <div className="flex flex-col items-center gap-3 py-16 text-destructive">
            <AlertCircle className="h-6 w-6" />
            <p className="text-sm">{error.message}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isPending && !error && !hasWorkspaces && (
          <div className="flex justify-center py-16">
            <div className="max-w-md rounded-xl border border-border bg-card p-8 text-center">
              <h2 className="text-lg font-medium text-foreground">Create your first workspace</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Build pages with AI — add tables, charts, and text.
              </p>
              <Button className="mt-4" onClick={onOpenNewWorkspace}>
                Create workspace
              </Button>
            </div>
          </div>
        )}

        {/* Workspace grid */}
        {!isPending && !error && hasWorkspaces && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {workspaces.map((ws) => (
              <WorkspaceCard
                key={ws.id}
                workspace={ws}
                onDelete={onDeleteWorkspace}
                onRename={onRenameWorkspace}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
